/**
 * Manage Insights — the registry tab: every registered insight pack
 * version, and the marketplace (LabMarketplace) that imports a published
 * release — the only way a pack is registered (the manual zip upload was
 * removed 2026-10-21).
 *
 * A pack is a zip of insight_pack.json, its SQL files and one HTML page
 * (docs/insight-packs.md), published by the insight-packs repo. Rows the earlier mechanism registered are listed
 * as legacy: nothing can build them, and remove is the one thing to do.
 */
import { Fragment, useCallback, useEffect, useState } from 'react'

import { ApiError } from '../lib/api'
import { buildPackEverywhere, packBundleUrl } from '../packs/api'
import { deletePack, listLabPacks, type LabPack } from '../packs/promotions'
import { compareVersions } from './LabBuilds'
import LabMarketplace from './LabMarketplace'
import LabReportOrder from './LabReportOrder'

const dash = (v: string | null | undefined) => (v && v.length ? v : '—')

const requiresSummary = (r: LabPack['requires']): string =>
  (r.tables ?? []).length ? (r.tables ?? []).map((t) => `table ${t}`).join(', ') : '—'

/** One group per pack name: its newest version (the publisher's order,
 *  LabBuilds.compareVersions — 1.10 above 1.9) and the older ones, newest
 *  first, folded under it (Ben, 2026-10-23). Groups keep name order. */
export function packGroups(packs: LabPack[]): { latest: LabPack; older: LabPack[] }[] {
  const byName = new Map<string, LabPack[]>()
  for (const p of packs) byName.set(p.name, [...(byName.get(p.name) ?? []), p])
  return [...byName.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, vs]) => {
      const sorted = [...vs].sort((a, b) => compareVersions(b.version, a.version))
      return { latest: sorted[0]!, older: sorted.slice(1) }
    })
}

export default function LabRegistry({ canInstall = false }: {
  /** Platform admins install from the marketplace (the server enforces it). */
  canInstall?: boolean
}) {
  const [packs, setPacks] = useState<LabPack[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const [openShape, setOpenShape] = useState<string | null>(null)
  // Pack names whose older versions are unfolded.
  const [openOlder, setOpenOlder] = useState<Set<string>>(new Set())
  const toggleOlder = (name: string) => setOpenOlder((prev) => {
    const next = new Set(prev)
    if (next.has(name)) next.delete(name)
    else next.add(name)
    return next
  })
  const [buildingAll, setBuildingAll] = useState<string | null>(null)
  const [buildAllMsg, setBuildAllMsg] = useState<string | null>(null)

  const buildEverywhere = (name: string, version: string) => {
    setBuildingAll(`${name}@${version}`)
    setBuildAllMsg(null)
    buildPackEverywhere(name, version)
      .then((r) => setBuildAllMsg(
        r.execution === 'in-flight'
          ? `${name}@${version}: ${r.note ?? 'a build is already running'} `
            + `(${r.served_games} served games)`
          : `${name}@${version}: queued ${r.queued} of ${r.served_games} served `
            + `game${r.served_games === 1 ? '' : 's'}`
            + (r.already_in_flight ? ` (${r.already_in_flight} already in flight)` : '')
            + ` — the builder is running; watch each game's pack card.`))
      .catch((e: unknown) => setBuildAllMsg(
        `${name}@${version}: ${e instanceof ApiError ? e.message : String(e)}`))
      .finally(() => setBuildingAll(null))
  }

  const load = useCallback(() => {
    setError(null)
    listLabPacks()
      .then((p) => setPacks(p.packs))
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
  }, [])

  useEffect(() => { load() }, [load])

  const confirmRemove = async (name: string, version: string) => {
    if (!window.confirm(`Remove insight pack ${name}@${version}?`)) return
    setRemoveError(null)
    try {
      await deletePack(name, version)
      load()
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.message : String(e)
      // Blocked by recorded builds: offer to remove those too.
      if (msg.includes('recorded build') &&
          window.confirm(`${msg}\n\nDelete every build of it and remove anyway?`)) {
        try {
          await deletePack(name, version, true)
          load()
        } catch (e2: unknown) {
          setRemoveError(e2 instanceof ApiError ? e2.message : String(e2))
        }
      } else {
        setRemoveError(msg)
      }
    }
  }

  /** A pack version's row (and its queries row). older: the latest
   *  version's folded-away versions, null on an older version's own row. */
  const packRow = (p: LabPack, older: LabPack[] | null) => {
    const key = `${p.name}@${p.version}`
    return (
      <Fragment key={key}>
        <tr className={older === null ? 'older-version' : undefined}>
          <td className="mono">
            {older === null ? <span className="muted">↳ {p.name}</span> : p.name}
          </td>
          <td className="num">
            {p.version}
            {older && older.length > 0 && (
              <div>
                <button className="linklike older-toggle"
                        aria-expanded={openOlder.has(p.name)}
                        title={older.map((o) => o.version).join(', ')}
                        onClick={() => toggleOlder(p.name)}>
                  {openOlder.has(p.name) ? '▾' : '▸'} {older.length} older
                </button>
              </div>
            )}
          </td>
          <td>
            {p.title && <div className="row-title">{p.title}</div>}
            {p.legacy
              ? <span className="muted">Legacy pack from the earlier mechanism — nothing can build it; remove it.</span>
              : dash(p.description)}
          </td>
          <td>{p.legacy ? '—' : requiresSummary(p.requires)}</td>
          <td className="mono">
            {p.legacy ? '—' : (
              <button className="linklike"
                      onClick={() => setOpenShape(openShape === key ? null : key)}>
                {p.data_shape.length} queries{' '}
                {openShape === key ? '▾ hide' : '· view'}
              </button>
            )}
          </td>
          <td>
            {p.legacy ? 'legacy' : p.source}
            {' '}
            {!p.legacy && (
              <a className="linklike"
                 href={packBundleUrl(p.name, p.version)}
                 title="Download the registered zip (SQL + page.html + source)">
                download zip
              </a>
            )}
            {' '}
            {!p.legacy && (
              <button className="linklike"
                      disabled={buildingAll === key}
                      title="Queue a build on every served game. Ineligible games fail with the missing tables named; a game with a published report whose data changed is skipped (rebuild it from the game to re-publish)."
                      onClick={() => buildEverywhere(p.name, p.version)}>
                {buildingAll === key ? 'queueing…' : 'build on all games'}
              </button>
            )}
            {' '}
            <button className="linklike remove"
                    onClick={() => confirmRemove(p.name, p.version)}>
              remove
            </button>
          </td>
        </tr>
        {openShape === key && (
          <tr>
            <td colSpan={6}>
              <pre className="shape-pre">{p.data_shape.join('\n')}</pre>
            </td>
          </tr>
        )}
      </Fragment>
    )
  }

  if (error) {
    return <div className="lab-error">Could not load the registry — {error}</div>
  }
  if (!packs) return <p className="muted">Loading…</p>

  return (
    <>
      {removeError && <div className="lab-error">{removeError}</div>}
      {buildAllMsg && <div className="lab-notice" role="status">{buildAllMsg}</div>}

      <section>
        <div className="section-head">
          <h2>Insight packs</h2>
        </div>
        <p className="lab-sub" style={{ marginBottom: 'var(--space-2)' }}>
          Each pack at its newest version; older versions fold under it.
          Packs come from the marketplace
          below. A pack lists on every served game with its eligibility, is
          built on a game the first time it is opened there, and is shown
          from the stored page after that.
        </p>
        {packs.length === 0 ? (
          <div className="lab-empty">No packs registered — import one from the marketplace below.</div>
        ) : (
          <table className="lab-table">
            <thead>
              <tr>
                <th>Name</th><th>Version</th><th>Title &amp; description</th>
                <th>Reads</th><th>Queries</th><th>Source</th>
              </tr>
            </thead>
            <tbody>
              {packGroups(packs).map(({ latest, older }) => (
                <Fragment key={latest.name}>
                  {packRow(latest, older)}
                  {openOlder.has(latest.name) && older.map((p) => packRow(p, null))}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <LabReportOrder canEdit={canInstall} />

      <LabMarketplace canInstall={canInstall} onImported={load} />
    </>
  )
}
